export const RECIPIENT_FILTERS = [
  { key: 'all', label: 'All customers', match: () => true },
  {
    key: 'past_due',
    label: 'Past due (any bucket)',
    match: (statement) =>
      (statement.aging?.d1_30 || 0) +
        (statement.aging?.d31_60 || 0) +
        (statement.aging?.d61_90 || 0) +
        (statement.aging?.d90_plus || 0) >
      0,
  },
  {
    key: 'd90_plus',
    label: '90+ days overdue',
    match: (statement) => (statement.aging?.d90_plus || 0) > 0,
  },
  {
    key: 'd61_90',
    label: '61-90 days overdue',
    match: (statement) => (statement.aging?.d61_90 || 0) > 0,
  },
  {
    key: 'd31_60',
    label: '31-60 days overdue',
    match: (statement) => (statement.aging?.d31_60 || 0) > 0,
  },
  {
    key: 'd1_30',
    label: '1-30 days overdue',
    match: (statement) => (statement.aging?.d1_30 || 0) > 0,
  },
  {
    key: 'in_credit',
    label: 'Has an unapplied credit',
    match: (statement) => (statement.aging?.current || 0) < 0,
  },
  {
    key: 'disagrees',
    label: 'Disagrees with QuickBooks',
    match: (statement) => statement.reconciles === false,
  },
  {
    key: 'paused',
    label: 'Statements paused',
    match: (statement) => statement.statementsPaused === true,
  },
  {
    key: 'unconfirmed',
    label: 'Not yet confirmed as a practice',
    match: (statement) => statement.isPractice === null,
  },
  {
    key: 'email_rejected',
    label: 'Email will be rejected',
    match: (statement) => statement.billingEmailUsable === false,
  },
];

export function compareBy(a, b, key) {
  const get = (row) => {
    if (key === 'customerName') return row.customerName || '';
    if (key === 'billingEmail') return row.billingEmail || '';
    if (key === 'totalOpen') return typeof row.totalOpen === 'number' ? row.totalOpen : 0;
    if (key === 'oldestDaysPastDue') {
      return typeof row.oldestDaysPastDue === 'number' ? row.oldestDaysPastDue : 0;
    }
    const value = row.aging?.[key];
    return typeof value === 'number' ? value : 0;
  };
  const x = get(a);
  const y = get(b);
  if (typeof x === 'string' || typeof y === 'string') {
    return String(x).localeCompare(String(y), 'en', { sensitivity: 'base' });
  }
  return x - y;
}

export function csvCell(value) {
  if (value === null || value === undefined) return '""';
  const text = String(value);
  const escaped = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${escaped.replace(/"/g, '""')}"`;
}

export const csvWasEscaped = (value) =>
  value !== null && value !== undefined && /^[=+\-@\t\r]/.test(String(value));

const csvAmount = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '';

const csvVerdict = (bad, good, condition, known) => (known ? (condition ? bad : good) : '');

export const RECIPIENT_COLUMNS = 9;

const CSV_COLUMNS = [
  ['Customer', (statement) => csvCell(statement.customerName)],
  ['Billing email', (statement) => csvCell(statement.billingEmail)],
  [
    'Address usable',
    (statement) =>
      csvCell(
        !statement.billingEmail?.trim()
          ? 'No address'
          : csvVerdict('Will be rejected', 'Yes', statement.billingEmailUsable === false, true)
      ),
  ],
  ['Currency', (statement) => csvCell(statement.currency || '')],
  ['Not overdue', (statement) => csvAmount(statement.aging?.current)],
  ['1-30 days', (statement) => csvAmount(statement.aging?.d1_30)],
  ['31-60 days', (statement) => csvAmount(statement.aging?.d31_60)],
  ['61-90 days', (statement) => csvAmount(statement.aging?.d61_90)],
  ['90+ days', (statement) => csvAmount(statement.aging?.d90_plus)],
  [
    'Oldest days past due',
    (statement) =>
      typeof statement.oldestDaysPastDue === 'number' ? String(statement.oldestDaysPastDue) : '',
  ],
  ['Total due', (statement) => csvAmount(statement.totalOpen)],
  ['Statements paused', (statement) => csvCell(statement.statementsPaused === true ? 'Yes' : 'No')],
  [
    'Confirmed as a practice',
    (statement) =>
      csvCell(
        csvVerdict('No', 'Yes', statement.isPractice !== true, statement.isPractice !== undefined)
      ),
  ],
  [
    'Agrees with QuickBooks',
    (statement) =>
      csvCell(
        csvVerdict(
          'No',
          'Yes',
          statement.reconciles === false,
          typeof statement.reconciles === 'boolean'
        )
      ),
  ],
];

export function buildStatementsCsv(rows) {
  const header = CSV_COLUMNS.map(([label]) => `"${label.replace(/"/g, '""')}"`).join(',');
  const body = rows.map((row) => CSV_COLUMNS.map(([, read]) => read(row)).join(','));
  return `﻿${[header, ...body].join('\r\n')}\r\n`;
}

export function csvFilename(asOf, { narrowed = false, partial = false } = {}) {
  const text = String(asOf);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : 'undated';
  return `statements-${date}${narrowed ? '-filtered' : ''}${partial ? '-partial' : ''}.csv`;
}
